import { useEffect } from 'react';

import { useAppState } from 'AppStateProvider';
import type { EnrichedContactWithCrm, RevealBulkResponse } from 'domain/contact';
import type { ContactsToExportPreviewReturn, Crm } from 'domain/crm';
import { usePreviewContactsQuery } from 'features/crm/previewModal/usePreviewContactsQuery';

const injectPreviewIntoContacts = (
  baseContacts: EnrichedContactWithCrm[],
  previewData: ContactsToExportPreviewReturn
): EnrichedContactWithCrm[] =>
  baseContacts.map((contact) => {
    const preview = previewData.find((p) => p.contactId === contact.id);
    return preview
      ? {
          ...contact,
          crmAccount: preview.matchedCompanies.length === 1 ? preview.matchedCompanies[0] : undefined,
          preview,
        }
      : contact;
  });

type UseContactsPreviewDataProps = {
  crm: Crm;
  isEnabled: boolean;
  enrichedContactsWithCRM: EnrichedContactWithCrm[];
  setEnrichedContactsWithCRM: (c: EnrichedContactWithCrm[]) => void;
  onConfirm: (props: { updatedContacts?: EnrichedContactWithCrm[]; revealResult?: RevealBulkResponse }) => void;
  revealResult?: RevealBulkResponse;
};

export const useContactsPreviewData = ({
  crm,
  isEnabled,
  enrichedContactsWithCRM,
  setEnrichedContactsWithCRM,
  onConfirm,
  revealResult,
}: UseContactsPreviewDataProps) => {
  const { selectedContacts } = useAppState();

  const { data, isLoading } = usePreviewContactsQuery({
    crm,
    contacts: selectedContacts,
    enabled: isEnabled && selectedContacts.length > 0 && selectedContacts.every((c) => c.isRevealed),
  });

  useEffect(() => {
    if (!data) {
      return;
    }

    const updatedContacts = injectPreviewIntoContacts(enrichedContactsWithCRM, data);
    setEnrichedContactsWithCRM(updatedContacts);

    const shouldAutoExport = updatedContacts.every((c) => c.crmAccount && !c.preview?.isExistAsLead);
    if (shouldAutoExport) {
      onConfirm({ updatedContacts, revealResult });
    }
  }, [data, revealResult]);

  return { data, isLoading };
};
